/* eslint-env browser */
import { DateTime } from "luxon";
import actions from "./config.json";
import actionWebInvoke from "./utils";
import { v4 as uuidv4 } from "uuid";

const TIMEZONES = {
  PT: "America/Los_Angeles",
  AET: "Australia/Sydney",
  BT: "Europe/London",
  CET: "Europe/Berlin",
  GMT: "Etc/GMT",
  IST: "Asia/Kolkata",
};

export function isValid(form) {
  const formValidation = validate(form);
  return Object.keys(formValidation).every((key) => formValidation[key].valid);
}

export function validate(form) {
  let validation = {
    eventStartTime: { valid: true },
    promoStartTime: { valid: true },
    promoEndTime: { valid: true },
    eventTitle: { valid: true },
    eventDescription: { valid: true },
    roles: { valid: true },
    skillLevels: { valid: true },
    eventType: { valid: true },
    eventLocation: { valid: true },
    eventSeries: { valid: true },
    speakers: { valid: true },
    productFocus: { valid: true },
    ctaLabel: { valid: true },
    ctaLink: { valid: true },
    email: { valid: true },
  };

  // Check if the event time is set and in the future
  let nowEventTime = DateTime.now().setZone(form.eventStartTime?.zone);
  if (!form.eventStartTime || !(form.eventStartTime instanceof DateTime)) {
    validation.eventStartTime = {
      valid: false,
      message: "Event start time is required.",
    };
  } else if (form.eventStartTime < nowEventTime) {
    validation.eventStartTime = {
      valid: false,
      message: "Event start time must be in the future.",
    };
  }

  if (!form.promoStartTime || !(form.promoStartTime instanceof DateTime)) {
    validation.promoStartTime = {
      valid: false,
      message: "Experience League event promotion start time is required.",
    };
  } else if (form.promoStartTime > form.eventStartTime) {
    validation.promoStartTime = {
      valid: false,
      message:
        "Experience League event promotion start time must be before the event start time.",
    };
  }

  if (!form.promoEndTime || !(form.promoEndTime instanceof DateTime)) {
    validation.promoEndTime = {
      valid: false,
      message: "Experience League event promotion end time is required.",
    };
  } else if (form.promoEndTime <= form.promoStartTime) {
    validation.promoEndTime = {
      valid: false,
      message:
        "Experience League event promotion end time must be after the start time.",
    };
  }

  if (!form.eventTitle) {
    validation.eventTitle = { valid: false, message: "Event title is required." };
  }

  if (!form.eventDescription) {
    validation.eventDescription = {
      valid: false,
      message: "Event description is required.",
    };
  } else if (form.eventDescription.length > 219) {
    validation.eventDescription = {
      valid: false,
      message: "Event description must be less than 219 characters.",
    };
  }

  // Validate event type
  if (!form.eventLocation?.type) {
    validation.eventType = {
      valid: false,
      message: "Event type selection is required.",
    };
  }

  // Validate event location (city) when type is in-person
  if (
    form.eventLocation?.type === "in-person" &&
    !form.eventLocation?.city?.trim()
  ) {
    validation.eventLocation = {
      valid: false,
      message: "Event location (city) is required for in-person events.",
    };
  }

  if (!form.productFocus || form.productFocus.length === 0) {
    validation.productFocus = {
      valid: false,
      message: "Primary product focus selection is required.",
    };
  }

  if (!form.ctaLabel) {
    validation.ctaLabel = {
      valid: false,
      message: "Event button label is required.",
    };
  }

  if (!form.ctaLink?.match(/^https?:\/\//)) {
    validation.ctaLink = {
      valid: false,
      message: "Event registration URL is required and must start with https.",
    };
  }

  if (!form.email?.match(/@adobe\.com$/)) {
    validation.email = {
      valid: false,
      message: "Event submitter email must be a valid Adobe email address.",
    };
  }

  // Validate speakers - if speakers are added, each must have a valid name
  if (form.speakers && form.speakers.length > 0) {
    const invalidSpeakers = form.speakers.filter(
      (speaker) => !speaker.name || !speaker.name.trim()
    );
    if (invalidSpeakers.length > 0) {
      validation.speakers = {
        valid: false,
        message: "All speakers must have a valid name.",
      };
    }
  }

  return validation;
}

export function getValidationMessages(form) {
  const messages = [];
  const formValidation = validate(form);

  messages.push(
    ...Object.keys(formValidation)
      .filter((key) => !formValidation[key].valid)
      .map((key) => formValidation[key].message)
  );

  return messages;
}

export async function submit(
  form,
  ims,
  oktaAuth,
  setData,
  profilePictures = {}
) {
  if (!isValid(form)) {
    return;
  }

  // Convert the form dates to UTC Strings
  form.eventTimezone = TIMEZONES[form.eventTimezone];
  form.promoStartTimezone = TIMEZONES[form.promoStartTimezone];
  form.promoEndTimezone = TIMEZONES[form.promoEndTimezone];

  try {
    setData({
      loading: true,
    });

    // Get and refresh Okta token once for all operations
    const originalOktaAccessToken = await oktaAuth.tokenManager.get(
      "accessToken"
    );
    let oktaAccessToken = await refreshToken(oktaAuth);

    if (!oktaAccessToken) {
      oktaAccessToken = originalOktaAccessToken;
    }

    // Step 1: Generate presigned URLs for speakers with profile pictures
    let presignedUrls = null;

    if (form.speakers && Object.keys(profilePictures).length > 0) {
      presignedUrls = await generatePresignedURLsForSpeakerImgs(
        form,
        ims,
        oktaAccessToken
      );
    }

    // Step 2: Upload profile pictures using presigned URLs
    if (presignedUrls && presignedUrls.speakers) {
      await uploadSpeakerImgs(form, presignedUrls, profilePictures);
    }

    // Step 3: Submit the form with all data including profile pictures
    oktaAccessToken = await refreshToken(oktaAuth);

    if (!oktaAccessToken) {
      oktaAccessToken = originalOktaAccessToken;
    }

    await submitEventData(form, ims, oktaAccessToken);

    setData({
      loading: false,
      success: [
        "Event submitted successfully! You will receive an email confirmation shortly.",
      ],
    });
  } catch (err) {
    // Backend errorResponse() always sends `{"error": "<message string>"}`,
    // not a nested `{error: {message}}` object; parse defensively in case the
    // body isn't JSON at all (e.g. an empty body) so we don't throw again here
    // and leave the UI stuck on "loading" with no error shown.
    let message = err.message;
    try {
      message = JSON.parse(
        err.message.substring(err.message.lastIndexOf("message:") + 9)
      )?.error;
    } catch (parseErr) {
      // response body wasn't JSON; fall back to the raw error message
    }

    setData({
      loading: false,
      error: [message],
    });

    return;
  }
}

/**
 * Generate presigned URLs for speaker profile pictures
 * @param {Object} form - The form data
 * @param {Object} ims - IMS object
 * @param {string} oktaAccessToken - Valid Okta access token
 * @returns {Object} Presigned URLs response
 */
async function generatePresignedURLsForSpeakerImgs(form, ims, oktaAccessToken) {
  const speakersWithIndices = form.speakers.map((speaker, index) => ({
    name: speaker.name || "",
    company: speaker.company || "",
    index: index.toString(),
  }));

  const eventId = uuidv4();

  // Store the eventId in the form so it can be used by the submit action
  form.eventId = eventId;

  const presignedUrls = await actionWebInvoke(
    actions["speaker-profile-pics"],
    getHeaders(ims, oktaAccessToken),
    {
      speakers: speakersWithIndices,
      eventId: eventId,
    }
  );

  return presignedUrls;
}

/**
 * Upload speaker profile pictures using presigned URLs
 * @param {Object} form - The form data (will be modified to include profile picture info)
 * @param {Object} presignedUrls - Presigned URLs response
 */
async function uploadSpeakerImgs(form, presignedUrls, profilePictures) {
  // Create an array of upload promises for parallel execution
  const uploadPromises = presignedUrls.speakers.map(async (speaker, i) => {
    if (
      speaker.profilePic &&
      speaker.profilePic.uploadUrl &&
      profilePictures[i]
    ) {
      try {
        // Upload the image using the presigned URL
        const uploadResponse = await fetch(speaker.profilePic.uploadUrl, {
          method: "PUT",
          body: profilePictures[i],
          headers: {
            "Content-Type": profilePictures[i].type,
          },
        });

        if (uploadResponse.ok) {
          // Store the profile picture information in the form
          const baseUrl = speaker.profilePic.uploadUrl.split("?")[0]; // Remove presigned parameters
          form.speakers[i].profilePictureURL = baseUrl;
          return { success: true, index: i };
        } else {
          console.error(
            `Failed to upload profile picture for speaker ${i}:`,
            uploadResponse.statusText
          );
          return { success: false, index: i, error: uploadResponse.statusText };
        }
      } catch (uploadError) {
        console.error(
          `Error uploading profile picture for speaker ${i}:`,
          uploadError
        );
        return { success: false, index: i, error: uploadError.message };
      }
    }
    return {
      success: false,
      index: i,
      error: "No profile picture or upload URL",
    };
  });

  // Execute all uploads in parallel
  const results = await Promise.all(uploadPromises);

  // Log summary of upload results
  const successfulUploads = results.filter((r) => r.success).length;
  const totalUploads = results.length;
  console.log(
    `Profile picture uploads: ${successfulUploads}/${totalUploads} successful`
  );
}

/**
 * Submit event data to the backend
 * @param {Object} form - The form data including profile picture information
 * @param {Object} ims - IMS object
 * @param {string} oktaAccessToken - Valid Okta access token
 * @returns {Object} Submit action response
 */
async function submitEventData(form, ims, oktaAccessToken) {
  const payload = { ...form };

  // Omit eventSeries entirely when "No series" was selected, rather than
  // sending an empty string, since the backend doesn't strip blank values.
  if (!payload.eventSeries) {
    delete payload.eventSeries;
  }

  const response = await actionWebInvoke(
    actions["submit"],
    getHeaders(ims, oktaAccessToken),
    payload
  );

  return response;
}

/**
 * Industry-standard headshot image validation
 * @param {File} file - The file to validate
 * @returns {Object} Validation result with isValid and errorMessage
 */
export function validateHeadshotImage(file) {
  return new Promise((resolve) => {
    // File type validation
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      resolve({
        isValid: false,
        errorMessage: "Please select a JPG or PNG image file",
      });
      return;
    }

    // File size validation (industry standard: 100KB-5MB for professional headshots)
    const minSize = 100 * 1024; // 100KB minimum
    const maxSize = 5 * 1024 * 1024; // 5MB maximum

    if (file.size < minSize) {
      resolve({
        isValid: false,
        errorMessage:
          "File is too small. Minimum size is 100KB for quality headshots.",
      });
      return;
    }

    if (file.size > maxSize) {
      resolve({
        isValid: false,
        errorMessage:
          "File is too large. Maximum size is 5MB. Please compress your image.",
      });
      return;
    }

    // Image dimensions validation (industry standard for headshots)
    // Check if we're in a browser environment
    if (typeof window === "undefined" || typeof document === "undefined") {
      resolve({
        isValid: false,
        errorMessage: "Image validation not available in this environment.",
      });
      return;
    }

    try {
      // Create image element for browser environment
      const img = document.createElement("img");
      const url = URL.createObjectURL(file);

      // Set timeout to prevent hanging
      const timeoutId = setTimeout(() => {
        URL.revokeObjectURL(url);
        resolve({
          isValid: false,
          errorMessage: "Image validation timed out. Please try again.",
        });
      }, 10000); // 10 second timeout

      img.onload = () => {
        try {
          clearTimeout(timeoutId);
          URL.revokeObjectURL(url);

          const { width, height } = img;

          // Minimum dimensions (industry standard: 200x200px minimum)
          if (width < 200 || height < 200) {
            resolve({
              isValid: false,
              errorMessage:
                "Image dimensions are too small. Minimum size is 200x200 pixels for quality headshots.",
            });
            return;
          }

          // Maximum dimensions (increased limit: 5000x5000px maximum)
          if (width > 5000 || height > 5000) {
            resolve({
              isValid: false,
              errorMessage:
                "Image dimensions are too large. Maximum size is 5000x5000 pixels. Please resize your image.",
            });
            return;
          }

          // Aspect ratio validation (flexible: 0.6-1.67 ratio for various orientations)
          // Calculate aspect ratio as width/height to get proper orientation values
          const aspectRatio = width / height;
          if (aspectRatio < 0.6 || aspectRatio > 1.67) {
            resolve({
              isValid: false,
              errorMessage: `Image aspect ratio ${aspectRatio.toFixed(
                2
              )} is outside acceptable range (0.6-1.67). Please use a properly proportioned image.`,
            });
            return;
          }

          resolve({
            isValid: true,
            errorMessage: null,
          });
        } catch (error) {
          URL.revokeObjectURL(url);
          resolve({
            isValid: false,
            errorMessage: "Error during image validation: " + error.message,
          });
        }
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(url);
        resolve({
          isValid: false,
          errorMessage:
            "Unable to read image file. Please ensure it's a valid image.",
        });
      };

      // Set image source (crossOrigin not needed for local blob URLs)
      img.src = url;
    } catch (error) {
      resolve({
        isValid: false,
        errorMessage: "Failed to initialize image validation: " + error.message,
      });
    }
  });
}

/**
 * Remove profile picture from form state
 * @param {number} index - Speaker index
 * @param {Function} setForm - Function to update form state
 */
export function removeProfilePicture(index, setForm) {
  setForm((prevForm) => ({
    ...prevForm,
    speakers: prevForm.speakers.map((s, i) =>
      i === parseInt(index)
        ? {
            ...s,
            profilePictureURL: undefined, // Remove profile picture URL
          }
        : s
    ),
  }));
}

/**
 * Get profile picture summary for form submission
 * @param {Array} speakers - Array of speakers with profile picture info
 * @returns {Object} Summary of profile pictures
 */
export function getProfilePictureSummary(speakers) {
  const summary = {
    totalSpeakers: speakers.length,
    speakersWithPics: 0,
    profilePics: [],
  };

  speakers.forEach((speaker, index) => {
    if (speaker.profilePictureURL) {
      summary.speakersWithPics++;
      summary.profilePics.push({
        speakerIndex: index,
        speakerName: speaker.name,
        url: speaker.profilePictureURL,
      });
    }
  });

  return summary;
}

export function eventSeriesThumbnail(series) {
  let thumbnail = "";
  const BASE_URL = "//cdn.experienceleague.adobe.com/thumb/";
  const DEFAULT_IMAGE = `${BASE_URL}exl-event-generic.png`;

  if (series) {
    const formattedSeries = series.replace(/ /g, "-").toLowerCase();
    thumbnail = `${BASE_URL}exl-event-${formattedSeries}.png`;
  } else {
    thumbnail = DEFAULT_IMAGE;
  }

  return thumbnail;
}

function getHeaders(ims, oktaAccessToken) {
  const headers = {};

  // set the authorization header and org from the ims props object
  if (typeof oktaAccessToken === "string") {
    headers.authorization = `Bearer ${oktaAccessToken}`;
  } else if (typeof oktaAccessToken === "object") {
    headers.authorization = `Bearer ${oktaAccessToken.accessToken}`;
  } else {
    // eslint-disable-next-line no-undef
    alert(
      "It appears that you are not logged in to Adobe Okta or your section has expired. This submission will likely fail. Please refresh, log in and try again."
    );
  }

  if (ims.org && !headers["x-gw-ims-org-id"]) {
    headers["x-gw-ims-org-id"] = ims.org;
  }

  return headers;
}

async function refreshToken(oktaAuth) {
  try {
    const tokenManager = oktaAuth.tokenManager;
    // Renew the token
    const token = await tokenManager.renew("accessToken");
    return token.accessToken;
  } catch (error) {
    console.error("Error refreshing token:", error);
  }
}
